import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";

import { GlossRoutes, NavItem } from "../../../models/enum/routes.enum";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";

const MAX_PINNED = 4;
const MIN_PINNED = 3;
const DRAG_START_THRESHOLD_PX = 6;
const RESET_SLIDE_MS = 300;

type ListName = "pinned" | "available";

export interface EditNavigationResult {
  order: NavItem[];
  pinnedCount: number;
}

interface DragState {
  item: NavItem;
  pointerId: number;
  element: HTMLElement;
  /** Pointer position relative to the row's top-left at drag start, so the row doesn't jump under the cursor. */
  grabOffsetX: number;
  grabOffsetY: number;
  width: number;
  /** True once the pointer has moved past the drag threshold and the row is visually "lifted". */
  started: boolean;
  startX: number;
  startY: number;
}

/** A second item that applyMove moved as a side effect: bumped out of a full Pinned list
 * ("evicted"), or pulled in from Available to keep Pinned at MIN_PINNED ("promoted"). */
interface DisplacedItem {
  item: NavItem;
  kind: "evicted" | "promoted";
}

function moveWithinArray<T>(items: T[], fromIndex: number, toIndex: number): void {
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
}

export function isPointInRect(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/** Given the rects of a list's current rows (in order, dragged row already excluded), finds the index the pointer's Y falls before. */
export function indexForY(rowRects: DOMRect[], pointerY: number): number {
  for (let i = 0; i < rowRects.length; i++) {
    const midpoint = rowRects[i].top + rowRects[i].height / 2;
    if (pointerY < midpoint) {
      return i;
    }
  }
  return rowRects.length;
}

@Component({
  selector: "app-edit-navigation",
  templateUrl: "./edit-navigation.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditNavigationComponent implements OnInit {
  @Input() pinnedItems: NavItem[] = [];
  @Input() otherItems: NavItem[] = [];
  /** The app's true factory-default nav order (unfiltered by any saved customization) — what
   * "Reset Order" restores, as opposed to just undoing changes made in this session. */
  @Input() defaultOrder: NavItem[] = [];
  @Output() saved = new EventEmitter<EditNavigationResult>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild("pinnedListEl") pinnedListEl!: ElementRef<HTMLElement>;
  @ViewChild("availableListEl") availableListEl!: ElementRef<HTMLElement>;

  protected pinned: NavItem[] = [];
  protected available: NavItem[] = [];
  protected draggingKey: string | null = null;
  protected dragTop = 0;
  protected dragLeft = 0;
  protected dragWidth = 0;
  /** Where the dragged item would land if released right now — drives the placeholder gap. */
  protected dropTargetList: ListName | null = null;
  protected dropTargetIndex: number | null = null;
  /** When dragging into a full pinned list, the item that would get bumped back to Available,
   * and the index it would land at there — drives the bump-preview ghost row. */
  protected bumpPreviewItem: NavItem | null = null;
  protected bumpPreviewIndex: number | null = null;

  private drag: DragState | null = null;

  private i18nService = inject(I18nService);

  /** Key of the item currently "picked up" via keyboard (Space/Enter) - null when nothing is
   * grabbed. Distinct from draggingKey, which only tracks pointer drags. */
  protected grabbedKey: string | null = null;
  /** Text for the visually-hidden aria-live region, announcing grab/move/drop for screen
   * reader users - the only feedback channel available for this interaction, since sighted
   * users see the row move but nothing here is otherwise exposed to assistive tech. */
  protected liveAnnouncement = "";

  ngOnInit(): void {
    this.pinned = [...this.pinnedItems];
    this.available = [...this.otherItems];
  }

  /** The Home tab is always first in the primary bar and can't be dragged out of it. */
  protected isLocked(item: NavItem): boolean {
    return item.key === GlossRoutes.Views;
  }

  /** Restores the app's true factory-default order (MAX_PINNED items, default sorting) —
   * not just whatever was loaded when this screen opened. */
  resetOrder(): void {
    if (this.defaultOrder.length === 0) {
      return;
    }
    const before = this.captureRowRects();
    this.grabbedKey = null; // the grabbed row is about to be replaced out from under the grab
    this.pinned = this.defaultOrder.slice(0, MAX_PINNED);
    this.available = this.defaultOrder.slice(MAX_PINNED);
    requestAnimationFrame(() => this.playSlideAnimation(before));
  }

  onPointerDown(event: PointerEvent, item: NavItem, rowEl: HTMLElement): void {
    if (this.drag || event.button !== 0 || this.isLocked(item)) {
      return;
    }
    const rect = rowEl.getBoundingClientRect();
    this.drag = {
      item,
      pointerId: event.pointerId,
      element: rowEl,
      grabOffsetX: event.clientX - rect.left,
      grabOffsetY: event.clientY - rect.top,
      width: rect.width,
      started: false,
      startX: event.clientX,
      startY: event.clientY,
    };
    rowEl.setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }

    if (!drag.started) {
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (Math.hypot(dx, dy) < DRAG_START_THRESHOLD_PX) {
        return;
      }
      drag.started = true;
      this.draggingKey = drag.item.key;
      this.dragWidth = drag.width;
    }

    this.dragLeft = event.clientX - drag.grabOffsetX;
    this.dragTop = event.clientY - drag.grabOffsetY;
    this.updateDropTarget(drag, event.clientX, event.clientY);
  }

  onPointerUp(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    // pointercancel (gesture interrupted, e.g. by the OS or browser chrome) auto-releases
    // capture before this fires — releasing again would throw and skip the cleanup below.
    if (drag.element.hasPointerCapture(event.pointerId)) {
      drag.element.releasePointerCapture(event.pointerId);
    }
    if (drag.started && this.dropTargetList !== null && this.dropTargetIndex !== null) {
      this.applyMove(drag.item, this.dropTargetList, this.dropTargetIndex);
    }
    this.drag = null;
    this.draggingKey = null;
    this.dropTargetList = null;
    this.dropTargetIndex = null;
    this.bumpPreviewItem = null;
    this.bumpPreviewIndex = null;
  }

  /**
   * Keyboard equivalent of the pointer drag (#4152). Space/Enter picks an item up or drops it;
   * while grabbed, arrow keys reorder it directly (each step commits immediately via
   * applyMove, unlike the pointer flow's preview-then-commit-on-release) and Escape releases
   * without moving further. Deliberate scope limit, matching dropdown-select's own precedent
   * (#4511 follow-up): each row is its own Tab stop rather than the full WAI-ARIA APG grid
   * pattern (single Tab stop + roving tabindex) - a real improvement over no keyboard path at
   * all, not full conformance.
   */
  protected onRowKeydown(event: KeyboardEvent, item: NavItem, list: ListName): void {
    if (this.isLocked(item)) {
      return;
    }
    const isGrabbed = this.grabbedKey === item.key;

    if (event.key === "Enter" || event.key === " ") {
      // preventDefault() must run even on a repeat, or the browser's Space-bar scroll fires on
      // every OS key-repeat tick; only the toggle is skipped (mirrors dropdown-select's trigger).
      event.preventDefault();
      if (event.repeat) {
        return;
      }
      if (isGrabbed) {
        this.grabbedKey = null;
        this.announce("navItemDropped", item.label);
      } else if (this.grabbedKey === null) {
        this.grabbedKey = item.key;
        this.announce("navItemPickedUp", item.label);
      } else {
        // Rejected, but not silent: otherwise the keypress just seems to do nothing.
        const grabbed = [...this.pinned, ...this.available].find((i) => i.key === this.grabbedKey);
        if (grabbed) {
          this.announce("navItemAlreadyGrabbed", grabbed.label);
        }
      }
      return;
    }

    if (!isGrabbed) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.grabbedKey = null;
      this.announce("navItemReleased", item.label);
      return;
    }

    const source = list === "pinned" ? this.pinned : this.available;
    const currentIndex = source.findIndex((i) => i.key === item.key);
    const minIndex = list === "pinned" ? 1 : 0;

    // Every arrow key is claimed while an item is grabbed - including moves rejected at a list
    // edge, so the overlay can't scroll out from under the row the user is still holding.
    if (!event.key.startsWith("Arrow")) {
      return;
    }
    event.preventDefault();

    if (event.key === "ArrowUp" && currentIndex > minIndex) {
      this.applyMove(item, list, currentIndex - 1);
      this.announceMove(item, list);
    } else if (event.key === "ArrowDown" && currentIndex < source.length - 1) {
      this.applyMove(item, list, currentIndex + 1);
      this.announceMove(item, list);
    } else if (event.key === "ArrowRight" && list === "available") {
      const displaced = this.applyMove(item, "pinned", this.pinned.length);
      this.announceMove(item, "pinned", displaced);
      this.refocusRow(item.key);
    } else if (event.key === "ArrowLeft" && list === "pinned") {
      const displaced = this.applyMove(item, "available", this.available.length);
      this.announceMove(item, "available", displaced);
      this.refocusRow(item.key);
    }
  }

  /** `displaced` is whatever else the move shifted between lists (see applyMove) - it is
   * appended to the same announcement so a single aria-live update carries both halves, rather
   * than a second update replacing the first before it has been read out. */
  private announceMove(item: NavItem, list: ListName, displaced: DisplacedItem[] = []): void {
    const source = list === "pinned" ? this.pinned : this.available;
    const position = source.findIndex((i) => i.key === item.key) + 1;
    const listLabel = this.i18nService.t(list === "pinned" ? "navListPinned" : "navListAvailable");
    // I18nService.t caps at 3 positional params (p1-p3) - "position of total" is folded into
    // one string here rather than passed as two separate params.
    const moved = this.message(
      "navItemMoved",
      item.label,
      `${position} / ${source.length}`,
      listLabel,
    );
    // Both side effects are about the Pinned list, whichever list the held item moved to.
    const pinnedLabel = this.i18nService.t("navListPinned");
    const sideEffects = displaced.map(({ item: other, kind }) =>
      kind === "evicted"
        ? this.message("navItemEvicted", other.label, pinnedLabel)
        : this.message("navItemPromoted", other.label, pinnedLabel, String(MIN_PINNED)),
    );
    this.setAnnouncement([moved, ...sideEffects].join(" "));
  }

  private announce(key: string, itemLabelKey: string, p2?: string, p3?: string): void {
    this.setAnnouncement(this.message(key, itemLabelKey, p2, p3));
  }

  /** The live region only announces a change, and Angular doesn't rewrite a text node whose bound
   * value is unchanged - so an identical repeat (e.g. pressing Space again on a row whose grab is
   * rejected) would be silent, the very symptom of a keypress that seems to do nothing. A trailing
   * no-break space, alternated on each repeat, makes the value differ without changing what is read. */
  private setAnnouncement(text: string): void {
    // String.fromCharCode rather than a literal: an invisible U+00A0 in the source is a trap.
    const noBreakSpace = String.fromCharCode(0xa0);
    this.liveAnnouncement = this.liveAnnouncement === text ? `${text}${noBreakSpace}` : text;
  }

  /** `itemLabelKey` is a NavItem.label value - itself an i18n key (see the row template's own
   * `item.label | i18n`), so it's translated here before being interpolated into `key`'s
   * message, rather than passing the raw key through untranslated. */
  private message(key: string, itemLabelKey: string, p2?: string, p3?: string): string {
    const itemLabel = this.i18nService.t(itemLabelKey);
    return this.i18nService.t(key, itemLabel, p2, p3);
  }

  /** Refocuses the row for `key` after a cross-list move - within a single list, @for's
   * track(item.key) reuses the same DOM node on reorder so focus survives automatically, but
   * pinned/available are separate @for blocks, so moving between them destroys and recreates
   * the row element and drops focus without this. */
  private refocusRow(key: string): void {
    // Matches resetOrder()'s own requestAnimationFrame use above - waits for Angular's change
    // detection and the resulting DOM update to actually flush before the row can be found.
    requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(`[data-key="${key}"]`);
      row?.focus();
    });
  }

  /** Recomputes — but doesn't yet apply — where the dragged item would land, so the placeholder
   * gap can preview the fit position live as the pointer moves. The actual array mutation only
   * happens once, in onPointerUp, so sibling rows don't jump around mid-drag. */
  private updateDropTarget(drag: DragState, pointerX: number, pointerY: number): void {
    const overPinned = isPointInRect(
      this.pinnedListEl.nativeElement.getBoundingClientRect(),
      pointerX,
      pointerY,
    );
    const overAvailable = isPointInRect(
      this.availableListEl.nativeElement.getBoundingClientRect(),
      pointerX,
      pointerY,
    );
    if (!overPinned && !overAvailable) {
      this.dropTargetList = null;
      this.dropTargetIndex = null;
      this.bumpPreviewItem = null;
      this.bumpPreviewIndex = null;
      return;
    }

    const targetList: ListName = overPinned ? "pinned" : "available";
    const containerEl = overPinned
      ? this.pinnedListEl.nativeElement
      : this.availableListEl.nativeElement;
    const rowRects = Array.from(containerEl.children)
      .filter((el) => el.hasAttribute("data-key") && el.getAttribute("data-key") !== drag.item.key)
      .map((el) => el.getBoundingClientRect());

    const minIndex = targetList === "pinned" ? 1 : 0; // slot 0 is reserved for the locked Home item
    this.dropTargetList = targetList;
    this.dropTargetIndex = Math.max(indexForY(rowRects, pointerY), minIndex);
    this.updateBumpPreview(drag.item, targetList, this.dropTargetIndex);
  }

  /** While dragging into a full pinned list, previews which pinned item would get bumped back
   * to Available and where it would land there — mirrors applyMove's swap branch, read-only. */
  private updateBumpPreview(item: NavItem, targetList: ListName, targetIndex: number): void {
    const fromPinned = this.pinned.some((i) => i.key === item.key);
    if (targetList !== "pinned" || fromPinned || this.pinned.length < MAX_PINNED) {
      this.bumpPreviewItem = null;
      this.bumpPreviewIndex = null;
      return;
    }

    const slot = Math.min(targetIndex, this.pinned.length - 1);
    const bumped = this.pinned[slot];
    if (this.isLocked(bumped)) {
      this.bumpPreviewItem = null;
      this.bumpPreviewIndex = null;
      return;
    }

    this.bumpPreviewItem = bumped;
    this.bumpPreviewIndex = this.available.findIndex((i) => i.key === item.key);
  }

  /** Moves `item` to `targetIndex` within `targetList`, reordering/transferring/swapping as needed. Pure array logic, no DOM.
   * Returns any other item the move bumped out of Pinned or pulled into it (empty when none),
   * so the keyboard path can announce it. */
  protected applyMove(item: NavItem, targetList: ListName, targetIndex: number): DisplacedItem[] {
    if (this.isLocked(item)) {
      return [];
    }
    // Index 0 of "pinned" is reserved for the locked Home item — nothing else may land there.
    const minIndex = targetList === "pinned" ? 1 : 0;
    const clampedTarget = Math.max(targetIndex, minIndex);

    const fromPinned = this.pinned.some((i) => i.key === item.key);
    const source = fromPinned ? this.pinned : this.available;
    const fromIndex = source.findIndex((i) => i.key === item.key);
    if (fromIndex === -1) {
      return [];
    }

    const sameList =
      (fromPinned && targetList === "pinned") || (!fromPinned && targetList === "available");
    if (sameList) {
      const boundedTarget = Math.min(clampedTarget, source.length - 1);
      if (boundedTarget !== fromIndex) {
        moveWithinArray(source, fromIndex, boundedTarget);
      }
      return [];
    }

    const displaced: DisplacedItem[] = [];
    if (targetList === "pinned") {
      if (this.pinned.length >= MAX_PINNED) {
        const slot = Math.min(clampedTarget, this.pinned.length - 1);
        const bumped = this.pinned[slot];
        if (this.isLocked(bumped)) {
          return [];
        }
        this.available.splice(fromIndex, 1, bumped);
        this.pinned.splice(slot, 1, item);
        displaced.push({ item: bumped, kind: "evicted" });
      } else {
        this.available.splice(fromIndex, 1);
        this.pinned.splice(Math.min(clampedTarget, this.pinned.length), 0, item);
      }
    } else {
      this.pinned.splice(fromIndex, 1);
      this.available.splice(Math.min(clampedTarget, this.available.length), 0, item);
      for (const promoted of this.backfillMinPinned()) {
        displaced.push({ item: promoted, kind: "promoted" });
      }
    }
    return displaced;
  }

  /** Pinned isn't allowed to drop below MIN_PINNED — rather than blocking the drag, silently
   * pull the next items back in from Available (in their current order) to top it back up.
   * Returns the items it pulled in. */
  private backfillMinPinned(): NavItem[] {
    const promotedItems: NavItem[] = [];
    while (this.pinned.length < MIN_PINNED && this.available.length > 0) {
      const [promoted] = this.available.splice(0, 1);
      this.pinned.push(promoted);
      promotedItems.push(promoted);
    }
    return promotedItems;
  }

  save(): void {
    this.saved.emit({
      order: [...this.pinned, ...this.available],
      pinnedCount: this.pinned.length,
    });
  }

  /** FLIP-technique setup: record each row's current position, keyed by NavItem key. */
  private captureRowRects(): Map<string, DOMRect> {
    const rects = new Map<string, DOMRect>();
    if (!this.pinnedListEl || !this.availableListEl) {
      return rects;
    }
    for (const containerEl of [
      this.pinnedListEl.nativeElement,
      this.availableListEl.nativeElement,
    ]) {
      for (const el of Array.from(containerEl.children)) {
        const key = el.getAttribute("data-key");
        if (key) {
          rects.set(key, el.getBoundingClientRect());
        }
      }
    }
    return rects;
  }

  /** FLIP-technique playback: for each row that moved, offset it back to its old spot with no
   * transition, then release the offset with a transition so it visibly slides into place. */
  private playSlideAnimation(before: Map<string, DOMRect>): void {
    if (!this.pinnedListEl || !this.availableListEl) {
      return;
    }
    for (const containerEl of [
      this.pinnedListEl.nativeElement,
      this.availableListEl.nativeElement,
    ]) {
      for (const el of Array.from(containerEl.children) as HTMLElement[]) {
        const key = el.getAttribute("data-key");
        const beforeRect = key && before.get(key);
        if (!beforeRect) {
          continue;
        }
        const afterRect = el.getBoundingClientRect();
        const dx = beforeRect.left - afterRect.left;
        const dy = beforeRect.top - afterRect.top;
        if (dx === 0 && dy === 0) {
          continue;
        }
        el.style.transition = "none";
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        void el.offsetWidth; // force reflow so the "no transition" jump is applied before we animate
        el.style.transition = `transform ${RESET_SLIDE_MS}ms ease`;
        el.style.transform = "";
        const cleanup = () => {
          el.style.transition = "";
          el.removeEventListener("transitionend", cleanup);
        };
        el.addEventListener("transitionend", cleanup);
      }
    }
  }
}
